import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router";
import { AcoesEmLote } from "../componentes/chamados/AcoesEmLote";
import { AvisosDaFila } from "../componentes/chamados/AvisosDaFila";
import { CamposDoChamado } from "../componentes/chamados/CamposDoChamado";
import { FormularioDeChamado } from "../componentes/chamados/FormularioDeChamado";
import { FormularioNoDetalhe } from "../componentes/chamados/FormularioNoDetalhe";
import { FiltroPorCampo } from "../componentes/chamados/FiltroPorCampo";
import { RelacoesDoChamado } from "../componentes/chamados/RelacoesDoChamado";
import { AprovacoesDoChamado } from "../componentes/chamados/AprovacoesDoChamado";
import { DespesasDoChamado } from "../componentes/chamados/DespesasDoChamado";
import { HorasDoChamado } from "../componentes/chamados/HorasDoChamado";
import { AtivosDoChamado } from "../componentes/ativos/AtivosDoChamado";
import { CompromissosDoChamado } from "../componentes/compromissos/CompromissosDoChamado";
import { EtiquetasDoChamado, FiltroDeTag } from "../componentes/chamados/EtiquetasDoChamado";
import { FechamentoDoCiclo } from "../componentes/chamados/FechamentoDoCiclo";
import { ParticipantesDoChamado } from "../componentes/chamados/ParticipantesDoChamado";
import { VisoesSalvas } from "../componentes/chamados/VisoesSalvas";
import { AssistenciaDeIa } from "../componentes/chamados/AssistenciaDeIa";
import { ArtigoAPartirDoChamado } from "../componentes/conhecimento";
import { MacrosDoChamado } from "../componentes/automacao/MacrosDoChamado";
import { IndicadorDeMarcas, MarcasDoChamado } from "../componentes/operacao/MarcasDoChamado";
import { PedirExportacao } from "../componentes/operacao/PedirExportacao";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../componentes/ui";
import { juntarRascunho } from "../nucleo/assistenciaIa";
import { mensagemDoConsole } from "../nucleo/console";
import { useAcessoAosChamados } from "../nucleo/repasse";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import {
  ROTULO_DA_URGENCIA,
  ROTULO_DO_ESTADO,
  caminhoDoAnexoDoTicket,
  rotuloDoCanal,
  useAgentesDeAtendimento,
  useAlterarTicket,
  useAnexarAoTicket,
  useCalendariosDeSla,
  useContratosDeSla,
  useCriarContratoDeSla,
  useCriarEquipeDeAtendimento,
  useCriarCalendarioDeSla,
  useCriarCategoriaDeTicket,
  useCriarPoliticaDeSla,
  useCriarServicoDeTicket,
  useCatalogoDeTickets,
  useEquipesDeAtendimento,
  usePainelDeSla,
  usePoliticasDeSla,
  useReconhecerAlertaDeSla,
  useRestaurarSlaAutomatico,
  useResponderTicket,
  useSobrescreverSla,
  useTicket,
  useTickets,
  type EstadoDoTicket,
  type Ticket,
  type UrgenciaDoTicket,
} from "../nucleo/tickets";
import { ROTULO_DA_COR, useServicosDisponiveis } from "../nucleo/operacao";
import { useTitulo } from "../nucleo/titulo";

function quando(valor: string): string {
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

function percentual(valor: number | null | undefined): string {
  return valor == null ? "—" : `${valor.toLocaleString("pt-BR")}%`;
}

function tomDoEstado(estado: EstadoDoTicket): "ok" | "atencao" | "erro" | "neutro" | "aguardando" {
  if (estado === "resolvido" || estado === "fechado") return "ok";
  if (estado === "novo") return "atencao";
  if (estado === "cancelado") return "neutro";
  return "aguardando";
}

function caminho(ticket: Ticket, interno: boolean) {
  return `${interno ? "/admin" : "/app"}/chamados/${ticket.id}`;
}

const ROTULO_DO_SLA: Record<string, string> = {
  sem_politica: "Sem política",
  no_prazo: "No prazo",
  proximo_do_limite: "Próximo do limite",
  pausado: "Pausado",
  violado: "Violado",
  cumprido: "Cumprido",
};

const DIAS_DO_EXPEDIENTE = [
  [0, "Seg"],
  [1, "Ter"],
  [2, "Qua"],
  [3, "Qui"],
  [4, "Sex"],
  [5, "Sáb"],
  [6, "Dom"],
] as const;

const ROTULO_DO_ALERTA = {
  aproximacao: "Próximo do limite",
  vencimento: "Prazo vencido",
  escalonamento: "Escalonado",
} as const;

const ROTULO_DO_MARCO = {
  primeira_resposta: "primeira resposta",
  solucao: "solução",
} as const;

function tomDoSla(estado: string): "ok" | "atencao" | "erro" | "neutro" | "aguardando" {
  if (estado === "cumprido" || estado === "no_prazo") return "ok";
  if (estado === "violado") return "erro";
  if (estado === "proximo_do_limite") return "atencao";
  if (estado === "pausado") return "aguardando";
  return "neutro";
}

export function ListaDeChamados({ interno = false }: { interno?: boolean }) {
  useTitulo("Chamados");
  const sessao = useSessao();
  const [texto, setTexto] = useState("");
  const [estado, setEstado] = useState<EstadoDoTicket | "">("");
  const [urgencia, setUrgencia] = useState<UrgenciaDoTicket | "">("");
  const [equipeId, setEquipeId] = useState("");
  const [responsavelId, setResponsavelId] = useState("");
  const [tag, setTag] = useState("");
  const [campoId, setCampoId] = useState("");
  const [campoValor, setCampoValor] = useState("");
  const [soFavoritos, setSoFavoritos] = useState(false);
  const [sinalizador, setSinalizador] = useState("");
  const [modo, setModo] = useState<"lista" | "kanban">("kanban");
  /* Painel de SLA, equipes e agentes são de `ticket:gerenciar`. O comercial é interno e tem só
     `ticket:ver`: pedir isso a ele seria uma fila de 403 na tela de quem só veio olhar. */
  const gerencia = interno && podeNaSessao(sessao.data, "ticket:gerenciar");
  const equipes = useEquipesDeAtendimento(gerencia);
  const agentes = useAgentesDeAtendimento(gerencia);
  const tickets = useTickets(
    interno
      ? {
          texto: texto.trim(),
          estado,
          urgencia,
          equipe_id: equipeId,
          responsavel_id: responsavelId,
          tag: tag.trim(),
          campo_id: campoValor.trim() ? campoId : "",
          campo_valor: campoValor.trim(),
          favoritos: soFavoritos ? "true" : "",
          sinalizador,
        }
      : {},
  );
  const podeAbrir = !interno && podeNaSessao(sessao.data, "ticket:abrir");
  /* O cliente em implantação só abre chamado depois do primeiro repasse de módulo. Ler e
     responder o que já existe nunca é bloqueado; a recusa de abrir é da API, e esta tela só a
     explica antes de a pessoa preencher um formulário que seria recusado. */
  const acesso = useAcessoAosChamados(!interno && podeNaSessao(sessao.data, "ticket:ver"));
  const bloqueado = acesso.data?.liberado === false;

  return (
    <div data-testid={interno ? "fila-de-chamados" : "chamados-do-cliente"}>
      <h1 className="titulo-pagina">{interno ? "Fila de chamados" : "Chamados"}</h1>
      <p className="lead">
        {interno
          ? "Solicitações recebidas das empresas, com prioridade e andamento em um só lugar."
          : "Abra uma solicitação e acompanhe toda a conversa com a equipe da GEOvendas."}
      </p>

      {gerencia ? <AvisosDaFila /> : null}

      {gerencia ? <PainelOperacionalDeSla /> : null}

      {podeAbrir && bloqueado ? (
        <Aviso tom="marca" className="mb-5" data-testid="chamados-bloqueados">
          <b>Abrir chamado ainda não está liberado.</b>{" "}
          {acesso.data?.motivo ??
            "O menu Chamados abre quando o primeiro módulo for repassado ao atendimento."}
        </Aviso>
      ) : null}

      {podeAbrir && !bloqueado ? <FormularioDeChamado /> : null}

      {interno ? (
        <Cartao titulo="Filtrar fila" className="mb-5">
          {gerencia ? (
            <VisoesSalvas
              filtros={{ texto, estado, urgencia, equipe_id: equipeId, responsavel_id: responsavelId, tag }}
              modo={modo}
              aoAplicar={(visao) => {
                setTexto(visao.filtros.texto ?? "");
                setEstado(visao.filtros.estado ?? "");
                setUrgencia(visao.filtros.urgencia ?? "");
                setEquipeId(visao.filtros.equipe_id ?? "");
                setResponsavelId(visao.filtros.responsavel_id ?? "");
                setTag(visao.filtros.tag ?? "");
                setModo(visao.modo);
              }}
            />
          ) : null}
          <div className="grid gap-3 md:grid-cols-3">
            <Campo rotulo="Buscar chamado" value={texto} onChange={(evento) => setTexto(evento.target.value)} placeholder="Número, assunto ou descrição" />
            <SeletorDeFiltro rotulo="Estado" valor={estado} aoMudar={(valor) => setEstado(valor as EstadoDoTicket | "")} opcoes={ROTULO_DO_ESTADO} />
            <SeletorDeFiltro rotulo="Urgência" valor={urgencia} aoMudar={(valor) => setUrgencia(valor as UrgenciaDoTicket | "")} opcoes={ROTULO_DA_URGENCIA} />
            {gerencia ? (
              <>
                <SeletorSimples rotulo="Equipe" valor={equipeId} aoMudar={setEquipeId} opcoes={equipes.data ?? []} />
                <SeletorSimples rotulo="Responsável" valor={responsavelId} aoMudar={setResponsavelId} opcoes={agentes.data ?? []} />
                <FiltroDeTag valor={tag} aoMudar={setTag} />
                <SeletorDeFiltro
                  rotulo="Sinalizador"
                  valor={sinalizador}
                  aoMudar={setSinalizador}
                  opcoes={{ qualquer: "Qualquer cor", ...ROTULO_DA_COR }}
                />
                <label className="campo flex items-end gap-2" htmlFor="filtro-favoritos">
                  <input id="filtro-favoritos" type="checkbox" checked={soFavoritos} onChange={(evento) => setSoFavoritos(evento.target.checked)} />
                  <span>Só os meus favoritos</span>
                </label>
                <FiltroPorCampo
                  campoId={campoId}
                  valor={campoValor}
                  aoMudar={(id, valor) => {
                    setCampoId(id);
                    setCampoValor(valor);
                  }}
                />
              </>
            ) : null}
            <div className="flex items-end gap-2">
              <Botao variante={modo === "kanban" ? "solido" : "fantasma"} onClick={() => setModo("kanban")}>Kanban</Botao>
              <Botao variante={modo === "lista" ? "solido" : "fantasma"} onClick={() => setModo("lista")}>Lista</Botao>
            </div>
          </div>
        </Cartao>
      ) : null}

      {interno ? (
        <div className="mb-4">
          <PedirExportacao
            rotulo="Exportar esta fila em segundo plano"
            pedido={{
              tipo: "fila",
              texto: texto.trim(),
              estado,
              urgencia,
              equipe_id: equipeId,
              responsavel_id: responsavelId,
              tag: tag.trim(),
              campo_id: campoValor.trim() ? campoId : "",
              campo_valor: campoValor.trim(),
              favoritos: soFavoritos || undefined,
              sinalizador,
            }}
          />
        </div>
      ) : null}

      {gerencia && tickets.data ? <AcoesEmLote tickets={tickets.data} /> : null}

      <section aria-labelledby="titulo-lista-chamados">
        <h2 className="titulo-secao" id="titulo-lista-chamados">
          {interno ? "Em acompanhamento" : "Meus chamados"}
        </h2>
        {tickets.isPending ? (
          <p role="status">Carregando chamados…</p>
        ) : tickets.isError ? (
          <Aviso tom="erro">{mensagemDoConsole(tickets.error)}</Aviso>
        ) : tickets.data.length === 0 ? (
          <Aviso tom="marca">Nenhum chamado aberto até agora.</Aviso>
        ) : interno && modo === "kanban" ? (
          <KanbanDeChamados tickets={tickets.data} />
        ) : (
          <div className="lista-de-itens">
            {tickets.data.map((ticket) => <CartaoDoTicket ticket={ticket} interno={interno} key={ticket.id} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function CartaoDoTicket({ ticket, interno }: { ticket: Ticket; interno: boolean }) {
  return (
    <Link className="cartao bloco-link" to={caminho(ticket, interno)} data-testid={`ticket-${ticket.numero}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <strong>#{ticket.numero} · {ticket.assunto}</strong> {interno ? <IndicadorDeMarcas ticket={ticket} /> : null}
          <p className="cartao__texto mt-1">
            {ROTULO_DA_URGENCIA[ticket.urgencia]} · atualizado em {quando(ticket.atualizado_em)}
          </p>
          {ticket.categoria ? <p className="nota-rodape mt-1">{ticket.categoria}</p> : null}
          {ticket.sla?.estado && (interno || ticket.sla.estado !== "violado") ? (
            <p className="nota-rodape mt-1">SLA: {ROTULO_DO_SLA[ticket.sla.estado] ?? ticket.sla.estado}</p>
          ) : null}
        </div>
        <Chip estado={tomDoEstado(ticket.estado)}>{ROTULO_DO_ESTADO[ticket.estado]}</Chip>
      </div>
    </Link>
  );
}

function PainelOperacionalDeSla() {
  const [inicioDoRelatorio, setInicioDoRelatorio] = useState("");
  const [fimDoRelatorio, setFimDoRelatorio] = useState("");
  const [organizacaoDoRelatorio, setOrganizacaoDoRelatorio] = useState("");
  const [servicoDoRelatorio, setServicoDoRelatorio] = useState("");
  const [equipeDoRelatorio, setEquipeDoRelatorio] = useState("");
  const [responsavelDoRelatorio, setResponsavelDoRelatorio] = useState("");
  const painelGeral = usePainelDeSla();
  const painel = usePainelDeSla({
    inicio: inicioDoRelatorio,
    fim: fimDoRelatorio,
    organizacao_id: organizacaoDoRelatorio,
    servico: servicoDoRelatorio,
    equipe_id: equipeDoRelatorio,
    responsavel_id: responsavelDoRelatorio,
  });
  const politicas = usePoliticasDeSla();
  const contratos = useContratosDeSla();
  const calendarios = useCalendariosDeSla();
  const equipes = useEquipesDeAtendimento();
  const agentes = useAgentesDeAtendimento();
  const catalogo = useCatalogoDeTickets();
  const criar = useCriarPoliticaDeSla();
  const criarContrato = useCriarContratoDeSla();
  const criarCalendario = useCriarCalendarioDeSla();
  const reconhecerAlerta = useReconhecerAlertaDeSla();
  const [filtro, setFiltro] = useState("");
  const [organizacaoId, setOrganizacaoId] = useState("");
  const [contratoId, setContratoId] = useState("");
  const [nome, setNome] = useState("");
  const [urgencia, setUrgencia] = useState<UrgenciaDoTicket | "">("");
  const [categoria, setCategoria] = useState("");
  const [servico, setServico] = useState("");
  const [calendarioId, setCalendarioId] = useState("");
  const [primeiraResposta, setPrimeiraResposta] = useState("60");
  const [solucao, setSolucao] = useState("480");
  const [alertaAntes, setAlertaAntes] = useState("60");
  const [escalonamentoApos, setEscalonamentoApos] = useState("30");
  const [calendarioOrganizacaoId, setCalendarioOrganizacaoId] = useState("");
  const [calendarioNome, setCalendarioNome] = useState("");
  const [fusoHorario, setFusoHorario] = useState("America/Sao_Paulo");
  const [diasDaSemana, setDiasDaSemana] = useState<number[]>([0, 1, 2, 3, 4]);
  const [horaInicio, setHoraInicio] = useState("08:00");
  const [horaFim, setHoraFim] = useState("18:00");
  const [feriados, setFeriados] = useState("");
  const [contratoOrganizacaoId, setContratoOrganizacaoId] = useState("");
  const [contratoNome, setContratoNome] = useState("");
  const [contratoPadrao, setContratoPadrao] = useState(false);
  const organizacoes = Array.from(
    new Map(
      painelGeral.data?.tickets.map((ticket) => [ticket.organizacao_id, ticket.organizacao]) ?? [],
    ),
  );
  const indicadores = painel.data
    ? [
        ["no_prazo", "No prazo", painel.data.no_prazo],
        ["proximo_do_limite", "Próximos", painel.data.proximo_do_limite],
        ["pausado", "Pausados", painel.data.pausado],
        ["violado", "Violados", painel.data.violado],
        ["cumprido", "Cumpridos", painel.data.cumprido],
        ["sem_politica", "Sem política", painel.data.sem_politica],
      ] as const
    : [];
  /* O chamado guarda o NOME do serviço, e é por ele que o relatório filtra (`servico=`). O valor
     da opção é o nome, então — mandar o id do catálogo não casava com chamado nenhum. Dois
     serviços de mesmo nome em categorias diferentes são o mesmo recorte e viram uma opção só. */
  const servicosDoRelatorio = Array.from(
    new Set((catalogo.servicos.data ?? []).map((item) => item.nome)),
  ).map((nomeDoServico) => ({ id: nomeDoServico, nome: nomeDoServico }));
  const tickets = painel.data?.tickets.filter(
    (ticket) => !filtro || ticket.estado_do_sla === filtro,
  ) ?? [];

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    criar.mutate(
      {
        organizacao_id: organizacaoId,
        nome: nome.trim(),
        urgencia: urgencia || null,
        categoria: categoria.trim() || null,
        servico: servico.trim() || null,
        contrato_id: contratoId || null,
        calendario_id: calendarioId || null,
        primeira_resposta_minutos: primeiraResposta ? Number(primeiraResposta) : null,
        solucao_minutos: solucao ? Number(solucao) : null,
        alerta_antes_minutos: alertaAntes ? Number(alertaAntes) : null,
        escalonamento_apos_minutos: escalonamentoApos ? Number(escalonamentoApos) : null,
      },
      { onSuccess: () => setNome("") },
    );
  }

  function submeterContrato(evento: FormEvent) {
    evento.preventDefault();
    criarContrato.mutate(
      {
        organizacao_id: contratoOrganizacaoId,
        nome: contratoNome.trim(),
        padrao: contratoPadrao,
      },
      {
        onSuccess: (contrato) => {
          setContratoNome("");
          setOrganizacaoId(contrato.organizacao_id);
          setContratoId(contrato.id);
        },
      },
    );
  }

  function submeterCalendario(evento: FormEvent) {
    evento.preventDefault();
    criarCalendario.mutate(
      {
        organizacao_id: calendarioOrganizacaoId,
        nome: calendarioNome.trim(),
        fuso_horario: fusoHorario.trim(),
        dias_da_semana: diasDaSemana,
        hora_inicio: horaInicio,
        hora_fim: horaFim,
        feriados: feriados.split(",").map((item) => item.trim()).filter(Boolean),
      },
      {
        onSuccess: (calendario) => {
          setCalendarioNome("");
          setOrganizacaoId(calendario.organizacao_id);
          setCalendarioId(calendario.id);
        },
      },
    );
  }

  return (
    <Cartao titulo="Painel de SLA" className="mb-5">
      {painel.isError ? <Aviso tom="erro">{mensagemDoConsole(painel.error)}</Aviso> : null}
      <h3 className="titulo-secao">Relatório histórico</h3>
      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="filtros-do-sla">
        <Campo rotulo="Data inicial" type="date" value={inicioDoRelatorio} onChange={(evento) => setInicioDoRelatorio(evento.target.value)} />
        <Campo rotulo="Data final" type="date" value={fimDoRelatorio} onChange={(evento) => setFimDoRelatorio(evento.target.value)} />
        <label className="campo" htmlFor="sla-relatorio-organizacao">
          <span className="campo__rotulo">Empresa</span>
          <select id="sla-relatorio-organizacao" className="campo__controle" value={organizacaoDoRelatorio} onChange={(evento) => setOrganizacaoDoRelatorio(evento.target.value)}>
            <option value="">Todas</option>
            {organizacoes.map(([id, nomeDaOrganizacao]) => <option value={id} key={id}>{nomeDaOrganizacao}</option>)}
          </select>
        </label>
        <SeletorSimples rotulo="Serviço do SLA" valor={servicoDoRelatorio} aoMudar={setServicoDoRelatorio} opcoes={servicosDoRelatorio} />
        <SeletorSimples rotulo="Equipe do SLA" valor={equipeDoRelatorio} aoMudar={setEquipeDoRelatorio} opcoes={equipes.data ?? []} />
        <SeletorSimples rotulo="Responsável do SLA" valor={responsavelDoRelatorio} aoMudar={setResponsavelDoRelatorio} opcoes={agentes.data ?? []} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Botao variante="fantasma" onClick={() => {
          setInicioDoRelatorio("");
          setFimDoRelatorio("");
          setOrganizacaoDoRelatorio("");
          setServicoDoRelatorio("");
          setEquipeDoRelatorio("");
          setResponsavelDoRelatorio("");
        }}>Limpar filtros</Botao>
        <span className="nota-rodape">{painel.data?.total ?? 0} chamado(s) no recorte</span>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="cumprimento-do-sla">
        <div className="rounded-xl border border-borda bg-superficie-suave p-3">
          <span className="nota-rodape">Primeira resposta no prazo</span>
          <strong className="block text-[28px] numero-tabular">
            {percentual(painel.data?.primeira_resposta_cumprimento_percentual)}
          </strong>
          <span className="nota-rodape">
            {painel.data?.primeira_resposta_no_prazo ?? 0} de {painel.data?.primeira_resposta_medida ?? 0} medidos
          </span>
        </div>
        <div className="rounded-xl border border-borda bg-superficie-suave p-3">
          <span className="nota-rodape">Solução no prazo</span>
          <strong className="block text-[28px] numero-tabular">
            {percentual(painel.data?.solucao_cumprimento_percentual)}
          </strong>
          <span className="nota-rodape">
            {painel.data?.solucao_no_prazo ?? 0} de {painel.data?.solucao_medida ?? 0} medidos
          </span>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6" data-testid="indicadores-de-sla">
        {indicadores.map(([chave, rotulo, valor]) => (
          <button
            className="rounded-xl border border-borda bg-superficie-suave p-3 text-left"
            type="button"
            onClick={() => setFiltro(filtro === chave ? "" : chave)}
            aria-pressed={filtro === chave}
            key={chave}
          >
            <span className="nota-rodape">{rotulo}</span>
            <strong className="block text-[28px] numero-tabular">{valor}</strong>
          </button>
        ))}
      </div>
      {filtro ? (
        <div className="lista-de-itens mt-3" aria-label={`Chamados com SLA ${ROTULO_DO_SLA[filtro]}`}>
          {tickets.length ? tickets.map((ticket) => (
            <Link className="bloco-link" to={`/admin/chamados/${ticket.id}`} key={ticket.id}>
              #{ticket.numero} · {ticket.assunto}
            </Link>
          )) : <p className="nota-rodape">Nenhum chamado neste indicador.</p>}
        </div>
      ) : null}

      <h3 className="titulo-secao mt-5">
        Alertas ativos {painel.data ? `(${painel.data.alertas_abertos})` : ""}
      </h3>
      <div className="lista-de-itens mt-3" data-testid="alertas-de-sla">
        {painel.data?.alertas.length ? painel.data.alertas.map((alerta) => (
          <div className="rounded-xl border border-borda bg-superficie-suave p-3" key={alerta.id}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <strong>{ROTULO_DO_ALERTA[alerta.tipo]} · {ROTULO_DO_MARCO[alerta.marco]}</strong>
                <p className="cartao__texto mt-1">
                  {alerta.organizacao} · #{alerta.numero_do_ticket} · {alerta.assunto_do_ticket}
                </p>
                <p className="nota-rodape mt-1">Limite: {quando(alerta.limite_em)}</p>
              </div>
              <div className="flex gap-2">
                <Link className="botao botao--fantasma" to={`/admin/chamados/${alerta.ticket_id}`}>
                  Abrir chamado
                </Link>
                <Botao
                  variante="fantasma"
                  onClick={() => reconhecerAlerta.mutate(alerta.id)}
                  disabled={reconhecerAlerta.isPending}
                >
                  Reconhecer
                </Botao>
              </div>
            </div>
          </div>
        )) : <p className="nota-rodape">Nenhum alerta ativo.</p>}
      </div>
      {reconhecerAlerta.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(reconhecerAlerta.error)}</Aviso> : null}

      <h3 className="titulo-secao mt-5">Calendários de atendimento</h3>
      <p className="nota-rodape mb-3">
        {calendarios.data?.length ?? 0} calendário(s) ativo(s). Defina expediente, fuso e feriados
        antes de associar a contagem a uma política.
      </p>
      <form className="grid gap-3 md:grid-cols-3" onSubmit={submeterCalendario}>
        <label className="campo" htmlFor="sla-calendario-organizacao">
          <span className="campo__rotulo">Organização do calendário</span>
          <select
            id="sla-calendario-organizacao"
            className="campo__controle"
            value={calendarioOrganizacaoId}
            onChange={(evento) => setCalendarioOrganizacaoId(evento.target.value)}
            required
          >
            <option value="">Selecione</option>
            {organizacoes.map(([id, nomeDaOrganizacao]) => (
              <option value={id} key={id}>{nomeDaOrganizacao}</option>
            ))}
          </select>
        </label>
        <Campo rotulo="Nome do calendário" value={calendarioNome} onChange={(evento) => setCalendarioNome(evento.target.value)} placeholder="Ex.: Comercial Brasil" minLength={2} required />
        <Campo rotulo="Fuso horário" value={fusoHorario} onChange={(evento) => setFusoHorario(evento.target.value)} required />
        <Campo rotulo="Início do expediente" type="time" value={horaInicio} onChange={(evento) => setHoraInicio(evento.target.value)} required />
        <Campo rotulo="Fim do expediente" type="time" value={horaFim} onChange={(evento) => setHoraFim(evento.target.value)} required />
        <Campo rotulo="Feriados (AAAA-MM-DD, separados por vírgula)" value={feriados} onChange={(evento) => setFeriados(evento.target.value)} placeholder="2026-12-25, 2027-01-01" />
        <fieldset className="campo md:col-span-2">
          <legend className="campo__rotulo">Dias de atendimento</legend>
          <div className="flex flex-wrap gap-3 pt-2">
            {DIAS_DO_EXPEDIENTE.map(([dia, rotulo]) => (
              <label className="flex items-center gap-1" key={dia}>
                <input
                  type="checkbox"
                  checked={diasDaSemana.includes(dia)}
                  onChange={(evento) => setDiasDaSemana((atuais) =>
                    evento.target.checked
                      ? [...atuais, dia].sort()
                      : atuais.filter((item) => item !== dia))}
                />
                {rotulo}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex items-end">
          <Botao type="submit" disabled={!calendarioOrganizacaoId || calendarioNome.trim().length < 2 || diasDaSemana.length === 0 || criarCalendario.isPending}>
            Criar calendário
          </Botao>
        </div>
      </form>
      {criarCalendario.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(criarCalendario.error)}</Aviso> : null}

      <h3 className="titulo-secao mt-5">Contratos de SLA</h3>
      <p className="nota-rodape mb-3">
        {contratos.data?.length ?? 0} contrato(s) ativo(s). O contrato agrupa as regras acordadas com cada empresa.
      </p>
      <form className="grid gap-3 md:grid-cols-3" onSubmit={submeterContrato}>
        <label className="campo" htmlFor="sla-contrato-organizacao">
          <span className="campo__rotulo">Organização do contrato</span>
          <select id="sla-contrato-organizacao" className="campo__controle" value={contratoOrganizacaoId} onChange={(evento) => setContratoOrganizacaoId(evento.target.value)} required>
            <option value="">Selecione</option>
            {organizacoes.map(([id, nomeDaOrganizacao]) => <option value={id} key={id}>{nomeDaOrganizacao}</option>)}
          </select>
        </label>
        <Campo rotulo="Nome do contrato" value={contratoNome} onChange={(evento) => setContratoNome(evento.target.value)} placeholder="Ex.: Contrato Ouro" minLength={2} required />
        <label className="flex items-center gap-2 self-end pb-3">
          <input type="checkbox" checked={contratoPadrao} onChange={(evento) => setContratoPadrao(evento.target.checked)} />
          Contrato padrão da empresa
        </label>
        <div><Botao type="submit" disabled={!contratoOrganizacaoId || contratoNome.trim().length < 2 || criarContrato.isPending}>Criar contrato</Botao></div>
      </form>
      {criarContrato.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(criarContrato.error)}</Aviso> : null}

      <h3 className="titulo-secao mt-5">Políticas</h3>
      <p className="nota-rodape mb-3">
        {politicas.data?.length ?? 0} política(s) ativa(s). Entre as regras aplicáveis, vale o prazo mais restritivo.
      </p>
      <form className="grid gap-3 md:grid-cols-3" onSubmit={submeter}>
        <label className="campo" htmlFor="sla-organizacao">
          <span className="campo__rotulo">Organização</span>
          <select id="sla-organizacao" className="campo__controle" value={organizacaoId} onChange={(evento) => setOrganizacaoId(evento.target.value)} required>
            <option value="">Selecione</option>
            {organizacoes.map(([id, nomeDaOrganizacao]) => <option value={id} key={id}>{nomeDaOrganizacao}</option>)}
          </select>
        </label>
        <Campo rotulo="Nome da política" value={nome} onChange={(evento) => setNome(evento.target.value)} placeholder="Ex.: Urgentes" minLength={2} required />
        <SeletorDeFiltro rotulo="Urgência da política" valor={urgencia} aoMudar={(valor) => setUrgencia(valor as UrgenciaDoTicket | "")} opcoes={ROTULO_DA_URGENCIA} />
        <Campo rotulo="Categoria (opcional)" value={categoria} onChange={(evento) => setCategoria(evento.target.value)} />
        <Campo rotulo="Serviço (opcional)" value={servico} onChange={(evento) => setServico(evento.target.value)} />
        <label className="campo" htmlFor="sla-contrato">
          <span className="campo__rotulo">Contrato (opcional)</span>
          <select id="sla-contrato" className="campo__controle" value={contratoId} onChange={(evento) => setContratoId(evento.target.value)}>
            <option value="">Sem contrato</option>
            {contratos.data?.filter((item) => item.organizacao_id === organizacaoId).map((item) => (
              <option value={item.id} key={item.id}>{item.nome}{item.padrao ? " · padrão" : ""}</option>
            ))}
          </select>
        </label>
        <label className="campo" htmlFor="sla-calendario">
          <span className="campo__rotulo">Contagem do prazo</span>
          <select id="sla-calendario" className="campo__controle" value={calendarioId} onChange={(evento) => setCalendarioId(evento.target.value)}>
            <option value="">Horas corridas</option>
            {calendarios.data?.filter((item) => item.organizacao_id === organizacaoId).map((item) => (
              <option value={item.id} key={item.id}>{item.nome} · {item.fuso_horario}</option>
            ))}
          </select>
        </label>
        <Campo rotulo="1ª resposta (min)" type="number" min={1} value={primeiraResposta} onChange={(evento) => setPrimeiraResposta(evento.target.value)} />
        <Campo rotulo="Solução (min)" type="number" min={1} value={solucao} onChange={(evento) => setSolucao(evento.target.value)} />
        <Campo rotulo="Alertar antes (min)" type="number" min={0} value={alertaAntes} onChange={(evento) => setAlertaAntes(evento.target.value)} />
        <Campo rotulo="Escalonar após vencer (min)" type="number" min={0} value={escalonamentoApos} onChange={(evento) => setEscalonamentoApos(evento.target.value)} />
        <div className="flex items-end"><Botao type="submit" disabled={!organizacaoId || !nome.trim() || (!primeiraResposta && !solucao) || criar.isPending}>Criar política</Botao></div>
      </form>
      {criar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(criar.error)}</Aviso> : null}
    </Cartao>
  );
}

function KanbanDeChamados({ tickets }: { tickets: Ticket[] }) {
  const estados = Object.keys(ROTULO_DO_ESTADO) as EstadoDoTicket[];
  return (
    <div className="grid gap-4 lg:grid-cols-3 2xl:grid-cols-6" data-testid="kanban-de-chamados">
      {estados.map((estado) => {
        const itens = tickets.filter((ticket) => ticket.estado === estado);
        return (
          <section className="rounded-xl border border-borda bg-superficie-suave p-3" key={estado}>
            <h3 className="mb-3 flex items-center justify-between gap-2 font-semibold">
              {ROTULO_DO_ESTADO[estado]} <span className="nota-rodape">{itens.length}</span>
            </h3>
            <div className="grid gap-3">
              {itens.length ? itens.map((ticket) => <CartaoDoTicket ticket={ticket} interno key={ticket.id} />) : <p className="nota-rodape">Nenhum chamado</p>}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function SeletorDeFiltro({ rotulo, valor, aoMudar, opcoes }: { rotulo: string; valor: string; aoMudar: (valor: string) => void; opcoes: Record<string, string> }) {
  const id = `filtro-${rotulo.toLocaleLowerCase("pt-BR")}`;
  return (
    <label className="campo" htmlFor={id}>
      <span className="campo__rotulo">{rotulo}</span>
      <select id={id} className="campo__controle" value={valor} onChange={(evento) => aoMudar(evento.target.value)}>
        <option value="">Todos</option>
        {Object.entries(opcoes).map(([chave, nome]) => <option value={chave} key={chave}>{nome}</option>)}
      </select>
    </label>
  );
}

function SeletorSimples({ rotulo, valor, aoMudar, opcoes }: { rotulo: string; valor: string; aoMudar: (valor: string) => void; opcoes: Array<{ id: string; nome: string }> }) {
  const id = `filtro-${rotulo.toLocaleLowerCase("pt-BR")}`;
  return (
    <label className="campo" htmlFor={id}>
      <span className="campo__rotulo">{rotulo}</span>
      <select id={id} className="campo__controle" value={valor} onChange={(evento) => aoMudar(evento.target.value)}>
        <option value="">Todos</option>
        {opcoes.map((item) => <option value={item.id} key={item.id}>{item.nome}</option>)}
      </select>
    </label>
  );
}

export function Chamado({ interno = false }: { interno?: boolean }) {
  const { id = "" } = useParams();
  useTitulo("Chamado");
  const sessao = useSessao();
  const ticket = useTicket(id);
  const podeResponder = podeNaSessao(sessao.data, "ticket:responder");
  const podeAnotar = interno && podeNaSessao(sessao.data, "ticket:nota_interna");
  const podeGerenciar = interno && podeNaSessao(sessao.data, "ticket:gerenciar");
  const podeParticipantes = podeNaSessao(sessao.data, "ticket:participantes");
  const podeApontar = interno && podeNaSessao(sessao.data, "horas:apontar");
  const podeFecharCiclo = !interno && podeNaSessao(sessao.data, "ticket:fechar_ciclo");
  const podeAssistir = interno && podeNaSessao(sessao.data, "ia:assistir");
  // O campo de resposta é daqui para o rascunho da IA poder entrar nele: a pessoa edita e envia.
  const [resposta, setResposta] = useState("");

  if (ticket.isPending) return <p role="status">Carregando chamado…</p>;
  if (ticket.isError) return <Aviso tom="erro">{mensagemDoConsole(ticket.error)}</Aviso>;

  const item = ticket.data;
  return (
    <div data-testid={`detalhe-ticket-${item.numero}`}>
      <p className="nota-rodape">Chamado #{item.numero}</p>
      <div className="flex items-start justify-between gap-3 mb-4">
        <h1 className="titulo-pagina mb-0">{item.assunto}</h1>
        <Chip estado={tomDoEstado(item.estado)}>{ROTULO_DO_ESTADO[item.estado]}</Chip>
      </div>
      {item.mesclado_em_numero ? (
        <Aviso tom="atencao" className="mb-4" data-testid="aviso-de-mescla">
          Este chamado foi mesclado no chamado{" "}
          <Link to={`${interno ? "/admin" : "/app"}/chamados/${item.mesclado_em_id ?? ""}`}>#{item.mesclado_em_numero}</Link>.
          A conversa continua lá e este aqui ficou só para consulta.
        </Aviso>
      ) : null}
      <p className="mb-3">
        <Link className="botao botao--fantasma" to={`${interno ? "/admin" : "/app"}/chamados/${item.id}/imprimir`}>
          Imprimir chamado
        </Link>
      </p>
      {interno ? <MarcasDoChamado ticket={item} podeMarcar={podeNaSessao(sessao.data, "ticket:marcar")} /> : null}
      <Cartao titulo="Solicitação" className="mb-4">
        <p className="texto-preservado">{item.descricao}</p>
        <p className="nota-rodape mt-3">
          Urgência {ROTULO_DA_URGENCIA[item.urgencia].toLocaleLowerCase("pt-BR")} · aberta em {quando(item.criado_em)}
        </p>
      </Cartao>

      <FormularioNoDetalhe ticketId={item.id} formulario={item.formulario} podeReprocessar={podeGerenciar} />

      <CamposDoChamado ticket={item} podeEditar={podeGerenciar && !item.mesclado_em_id} />

      <FechamentoDoCiclo ticketId={item.id} estado={item.estado} podeFecharCiclo={podeFecharCiclo} />

      {item.sla ? (
        <Cartao
          titulo="SLA"
          acessorio={item.sla.estado && (interno || item.sla.estado !== "violado") ? <Chip estado={tomDoSla(item.sla.estado)}>{ROTULO_DO_SLA[item.sla.estado]}</Chip> : undefined}
          className="mb-4"
        >
          <p className="cartao__texto">
            Primeira resposta: {item.sla.primeira_resposta_em ? `realizada em ${quando(item.sla.primeira_resposta_em)}` : item.sla.primeira_resposta_limite_em ? `até ${quando(item.sla.primeira_resposta_limite_em)}` : "sem prazo"}
          </p>
          <p className="cartao__texto mt-1">
            Solução: {item.sla.solucao_em ? `realizada em ${quando(item.sla.solucao_em)}` : item.sla.solucao_limite_em ? `até ${quando(item.sla.solucao_limite_em)}` : "sem prazo"}
          </p>
          {item.sla.sobrescrito_em ? (
            <Aviso tom="atencao" className="mt-3">
              Prazo ajustado manualmente em {quando(item.sla.sobrescrito_em)}.
              {/* O motivo só chega a quem é da equipe; para o cliente ele vem nulo. */}
              {item.sla.sobrescricao_motivo ? ` Motivo: ${item.sla.sobrescricao_motivo}` : null}
            </Aviso>
          ) : null}
          {podeGerenciar ? <GestaoDoSla ticket={item} /> : null}
        </Cartao>
      ) : null}

      <AprovacoesDoChamado ticketId={item.id} />

      <AnexosDoChamado ticket={item} podeEnviar={podeResponder} interno={interno} />

      <ParticipantesDoChamado ticketId={item.id} interno={interno} podeGerenciar={podeParticipantes} />

      {podeApontar ? (
        <>
          <HorasDoChamado ticketId={item.id} podeGerenciar={podeNaSessao(sessao.data, "horas:gerenciar")} />
          <DespesasDoChamado ticketId={item.id} podeGerenciar={podeNaSessao(sessao.data, "horas:gerenciar")} />
        </>
      ) : null}
      <AtivosDoChamado ticketId={item.id} organizacaoId={item.organizacao_id} />
      {interno ? <CompromissosDoChamado ticketId={item.id} numero={item.numero} /> : null}

      {podeGerenciar ? <EtiquetasDoChamado ticketId={item.id} /> : null}
      {podeGerenciar ? <RelacoesDoChamado ticketId={item.id} /> : null}
      {interno ? <ArtigoAPartirDoChamado ticketId={item.id} acoes={item.acoes} /> : null}

      {podeGerenciar ? <GestaoDoChamado ticket={item} /> : null}
      {interno && podeNaSessao(sessao.data, "macro:usar") ? <MacrosDoChamado ticketId={item.id} /> : null}

      {podeAssistir ? (
        <AssistenciaDeIa
          ticketId={item.id}
          podeSugerir={podeResponder && !item.mesclado_em_id}
          aoSugerirResposta={(rascunho) => setResposta((atual) => juntarRascunho(atual, rascunho))}
        />
      ) : null}

      <section className="mb-5" aria-labelledby="titulo-conversa">
        <h2 className="titulo-secao" id="titulo-conversa">Conversa</h2>
        {item.acoes.length === 0 ? (
          <Aviso tom="marca">Ainda não há respostas neste chamado.</Aviso>
        ) : (
          <div className="lista-de-itens">
            {item.acoes.map((acao) => (
              <Cartao
                key={acao.id}
                titulo={acao.tipo === "interna" ? "Nota interna" : acao.autor_email ?? "Equipe GEOvendas"}
                acessorio={acao.tipo === "interna" ? <Chip estado="neutro">Só equipe</Chip> : undefined}
              >
                <p className="texto-preservado">{acao.texto}</p>
                <p className="nota-rodape mt-2">
                  {quando(acao.criada_em)}
                  {rotuloDoCanal(acao.canal) ? ` · ${rotuloDoCanal(acao.canal)}` : ""}
                  {acao.origem_numero ? ` · veio do chamado #${acao.origem_numero}` : ""}
                </p>
              </Cartao>
            ))}
          </div>
        )}
      </section>

      {podeResponder && !item.mesclado_em_id ? (
        <FormularioDeMensagem ticketId={item.id} texto={resposta} aoMudarTexto={setResposta} />
      ) : null}
      {podeAnotar && !item.mesclado_em_id ? <FormularioDeMensagem ticketId={item.id} interna /> : null}
    </div>
  );
}

function GestaoDoSla({ ticket }: { ticket: Ticket }) {
  const sobrescrever = useSobrescreverSla(ticket.id);
  const restaurar = useRestaurarSlaAutomatico(ticket.id);
  const [primeiraResposta, setPrimeiraResposta] = useState("");
  const [solucao, setSolucao] = useState("");
  const [motivo, setMotivo] = useState("");

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    sobrescrever.mutate({
      primeira_resposta_minutos: primeiraResposta ? Number(primeiraResposta) : null,
      solucao_minutos: solucao ? Number(solucao) : null,
      motivo: motivo.trim(),
    });
  }

  return (
    <div className="mt-4 border-t border-borda pt-4">
      <h3 className="titulo-secao">Exceção de prazo</h3>
      <p className="nota-rodape mb-3">Defina novos limites a partir de agora. A justificativa fica no histórico interno.</p>
      <form className="grid gap-3 md:grid-cols-3" onSubmit={submeter}>
        <Campo rotulo="Nova 1ª resposta (min)" type="number" min={1} value={primeiraResposta} onChange={(evento) => setPrimeiraResposta(evento.target.value)} />
        <Campo rotulo="Nova solução (min)" type="number" min={1} value={solucao} onChange={(evento) => setSolucao(evento.target.value)} />
        <Campo rotulo="Motivo do ajuste" value={motivo} onChange={(evento) => setMotivo(evento.target.value)} minLength={3} required />
        <div className="flex gap-2 md:col-span-3">
          <Botao type="submit" disabled={(!primeiraResposta && !solucao) || motivo.trim().length < 3 || sobrescrever.isPending}>Aplicar exceção</Botao>
          {ticket.sla?.sobrescrito_em ? (
            <Botao type="button" variante="fantasma" onClick={() => restaurar.mutate()} disabled={restaurar.isPending}>
              Voltar ao cálculo automático
            </Botao>
          ) : null}
        </div>
      </form>
      {sobrescrever.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(sobrescrever.error)}</Aviso> : null}
      {restaurar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(restaurar.error)}</Aviso> : null}
    </div>
  );
}

function FormularioDeMensagem({
  ticketId,
  interna = false,
  texto: textoDoPai,
  aoMudarTexto,
}: {
  ticketId: string;
  interna?: boolean;
  /** Campo controlado por quem chama (a resposta pública, que a IA pode preencher). */
  texto?: string;
  aoMudarTexto?: (texto: string) => void;
}) {
  const enviar = useResponderTicket(ticketId, interna);
  const [textoLocal, setTextoLocal] = useState("");
  const texto = textoDoPai ?? textoLocal;
  const setTexto = aoMudarTexto ?? setTextoLocal;

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    enviar.mutate(texto.trim(), { onSuccess: () => setTexto("") });
  }

  return (
    <Cartao titulo={interna ? "Adicionar nota interna" : "Responder"} className="mb-4">
      <form onSubmit={submeter}>
        <CampoLongo
          rotulo={interna ? "Nota para a equipe" : "Mensagem"}
          ajuda={interna ? "O cliente não vê esta nota." : undefined}
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          required
        />
        {enviar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(enviar.error)}</Aviso> : null}
        <Botao type="submit" variante={interna ? "fantasma" : "solido"} disabled={!texto.trim() || enviar.isPending}>
          {enviar.isPending ? "Enviando…" : interna ? "Salvar nota" : "Enviar resposta"}
        </Botao>
      </form>
    </Cartao>
  );
}

function GestaoDoChamado({ ticket }: { ticket: Ticket }) {
  const alterar = useAlterarTicket(ticket.id);
  const equipes = useEquipesDeAtendimento();
  const agentes = useAgentesDeAtendimento();
  const criarEquipe = useCriarEquipeDeAtendimento();
  const catalogo = useCatalogoDeTickets();
  const criarCategoria = useCriarCategoriaDeTicket();
  const criarServico = useCriarServicoDeTicket();
  const [estado, setEstado] = useState<EstadoDoTicket>(ticket.estado);
  const [equipeId, setEquipeId] = useState(ticket.equipe_id ?? "");
  const [responsavelId, setResponsavelId] = useState(ticket.responsavel_id ?? "");
  const [categoria, setCategoria] = useState(ticket.categoria ?? "");
  const [servico, setServico] = useState(ticket.servico ?? "");
  const [novaEquipe, setNovaEquipe] = useState("");
  const [novaCategoria, setNovaCategoria] = useState("");
  const [novoServico, setNovoServico] = useState("");
  const categoriaSelecionada = catalogo.categorias.data?.find((item) => item.nome === categoria);
  /* Serviço restrito a outras organizações ou equipes não é oferecido; o servidor também o recusa. */
  const liberados = useServicosDisponiveis(ticket.organizacao_id);
  const idsLiberados = liberados.data ? new Set(liberados.data.map((item) => item.id)) : null;
  const servicosDaCategoria = (catalogo.servicos.data ?? []).filter(
    (item) =>
      item.categoria_id === categoriaSelecionada?.id && (!idsLiberados || idsLiberados.has(item.id)),
  );
  return (
    <Cartao titulo="Andamento" className="mb-4">
      <div className="flex items-end gap-3 flex-wrap">
        <label className="campo flex-1" htmlFor="estado-do-chamado">
          <span className="campo__rotulo">Estado</span>
          <select
            id="estado-do-chamado"
            className="campo__controle"
            value={estado}
            onChange={(evento) => setEstado(evento.target.value as EstadoDoTicket)}
          >
            {Object.entries(ROTULO_DO_ESTADO).map(([valor, rotulo]) => (
              <option value={valor} key={valor}>{rotulo}</option>
            ))}
          </select>
        </label>
        <label className="campo flex-1" htmlFor="equipe-do-chamado">
          <span className="campo__rotulo">Equipe</span>
          <select
            id="equipe-do-chamado"
            className="campo__controle"
            value={equipeId}
            onChange={(evento) => setEquipeId(evento.target.value)}
          >
            <option value="">Sem equipe</option>
            {equipes.data?.map((equipe) => <option value={equipe.id} key={equipe.id}>{equipe.nome}</option>)}
          </select>
        </label>
        <label className="campo flex-1" htmlFor="responsavel-do-chamado">
          <span className="campo__rotulo">Responsável</span>
          <select
            id="responsavel-do-chamado"
            className="campo__controle"
            value={responsavelId}
            onChange={(evento) => setResponsavelId(evento.target.value)}
          >
            <option value="">Sem responsável</option>
            {agentes.data?.map((agente) => <option value={agente.id} key={agente.id}>{agente.nome}</option>)}
          </select>
        </label>
        <label className="campo flex-1" htmlFor="categoria-do-chamado">
          <span className="campo__rotulo">Categoria</span>
          <select id="categoria-do-chamado" className="campo__controle" value={categoria} onChange={(evento) => { setCategoria(evento.target.value); setServico(""); }}>
            <option value="">Sem categoria</option>
            {categoria && !categoriaSelecionada ? <option value={categoria}>{categoria}</option> : null}
            {catalogo.categorias.data?.map((item) => <option value={item.nome} key={item.id}>{item.nome}</option>)}
          </select>
        </label>
        <label className="campo flex-1" htmlFor="servico-do-chamado">
          <span className="campo__rotulo">Serviço</span>
          <select id="servico-do-chamado" className="campo__controle" value={servico} onChange={(evento) => setServico(evento.target.value)} disabled={!categoriaSelecionada}>
            <option value="">Sem serviço</option>
            {servico && !servicosDaCategoria.some((item) => item.nome === servico) ? <option value={servico}>{servico}</option> : null}
            {servicosDaCategoria.map((item) => <option value={item.nome} key={item.id}>{item.nome}</option>)}
          </select>
        </label>
        <Botao
          onClick={() => alterar.mutate({
            estado,
            equipe_id: equipeId || null,
            responsavel_id: responsavelId || null,
            categoria: categoria.trim() || null,
            servico: servico.trim() || null,
          })}
          disabled={alterar.isPending}
        >
          Salvar distribuição
        </Botao>
      </div>
      <form
        className="flex items-end gap-3 flex-wrap mt-3"
        onSubmit={(evento) => {
          evento.preventDefault();
          criarEquipe.mutate(novaEquipe.trim(), {
            onSuccess: (equipe) => { setEquipeId(equipe.id); setNovaEquipe(""); },
          });
        }}
      >
        <Campo
          rotulo="Nova equipe"
          value={novaEquipe}
          onChange={(evento) => setNovaEquipe(evento.target.value)}
          placeholder="Ex.: Suporte técnico"
          minLength={2}
        />
        <Botao type="submit" variante="fantasma" disabled={novaEquipe.trim().length < 2 || criarEquipe.isPending}>
          Criar equipe
        </Botao>
      </form>
      {alterar.isError ? <Aviso tom="erro">{mensagemDoConsole(alterar.error)}</Aviso> : null}
      {criarEquipe.isError ? <Aviso tom="erro">{mensagemDoConsole(criarEquipe.error)}</Aviso> : null}
      <form className="flex items-end gap-3 flex-wrap mt-3" onSubmit={(evento) => {
        evento.preventDefault();
        criarCategoria.mutate(novaCategoria.trim(), { onSuccess: (item) => { setCategoria(item.nome); setNovaCategoria(""); } });
      }}>
        <Campo rotulo="Nova categoria" value={novaCategoria} onChange={(evento) => setNovaCategoria(evento.target.value)} placeholder="Ex.: Pedidos" minLength={2} />
        <Botao type="submit" variante="fantasma" disabled={novaCategoria.trim().length < 2 || criarCategoria.isPending}>Criar categoria</Botao>
      </form>
      <form className="flex items-end gap-3 flex-wrap mt-3" onSubmit={(evento) => {
        evento.preventDefault();
        if (categoriaSelecionada) criarServico.mutate({ categoriaId: categoriaSelecionada.id, nome: novoServico.trim() }, { onSuccess: (item) => { setServico(item.nome); setNovoServico(""); } });
      }}>
        <Campo rotulo="Novo serviço" value={novoServico} onChange={(evento) => setNovoServico(evento.target.value)} placeholder="Ex.: Emissão de pedido" minLength={2} />
        <Botao type="submit" variante="fantasma" disabled={!categoriaSelecionada || novoServico.trim().length < 2 || criarServico.isPending}>Criar serviço</Botao>
      </form>
      {criarCategoria.isError ? <Aviso tom="erro">{mensagemDoConsole(criarCategoria.error)}</Aviso> : null}
      {criarServico.isError ? <Aviso tom="erro">{mensagemDoConsole(criarServico.error)}</Aviso> : null}
    </Cartao>
  );
}

function AnexosDoChamado({
  ticket,
  podeEnviar,
  interno,
}: {
  ticket: Ticket;
  podeEnviar: boolean;
  interno: boolean;
}) {
  return (
    <Cartao titulo="Anexos" className="mb-4">
      {ticket.anexos.length === 0 ? (
        <p className="nota-rodape">Nenhum arquivo anexado.</p>
      ) : (
        <ul className="lista-de-itens mb-3">
          {ticket.anexos.map((anexo) => (
            <li key={anexo.id}>
              <a href={caminhoDoAnexoDoTicket(ticket.id, anexo.id)} download>
                {anexo.nome}
              </a>{" "}
              {anexo.interno ? <Chip estado="neutro">Só equipe</Chip> : null}
              {anexo.origem_numero ? <span className="nota-rodape"> · veio do chamado #{anexo.origem_numero}</span> : null}
            </li>
          ))}
        </ul>
      )}
      {podeEnviar ? <FormularioDeAnexo ticketId={ticket.id} /> : null}
      {podeEnviar && interno ? <FormularioDeAnexo ticketId={ticket.id} interno /> : null}
    </Cartao>
  );
}

function FormularioDeAnexo({ ticketId, interno = false }: { ticketId: string; interno?: boolean }) {
  const anexar = useAnexarAoTicket(ticketId, interno);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const seletor = useRef<HTMLInputElement>(null);
  return (
    <form
      className="flex items-end gap-3 flex-wrap mt-3"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (arquivo)
          anexar.mutate(arquivo, {
            onSuccess: () => {
              // O seletor guarda o arquivo escolhido: sem limpar, o mesmo arquivo não dispara onChange.
              setArquivo(null);
              if (seletor.current) seletor.current.value = "";
            },
          });
      }}
      data-testid={interno ? "anexo-interno" : "anexo-publico"}
    >
      <label className="campo flex-1">
        <span className="campo__rotulo">{interno ? "Arquivo só para a equipe" : "Arquivo para a conversa"}</span>
        <input
          className="campo__controle"
          type="file"
          ref={seletor}
          accept=".pdf,.docx,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.zip"
          onChange={(evento) => setArquivo(evento.target.files?.[0] ?? null)}
        />
      </label>
      <Botao type="submit" variante={interno ? "fantasma" : "solido"} disabled={!arquivo || anexar.isPending}>
        {anexar.isPending ? "Enviando…" : "Anexar"}
      </Botao>
      {anexar.isError ? <Aviso tom="erro">{mensagemDoConsole(anexar.error)}</Aviso> : null}
    </form>
  );
}
